import { useMemo, useState } from "react";
import { MapPin, Sparkles } from "lucide-react";
import { PRESENCE, BRAND } from "@/lib/data";
import { FadeIn } from "@/lib/hooks";

// Flatten grouped presence into a single ordered list. Order is preserved
// (Thailand → Cambodia → Vietnam → India) which the user requested.
function flattenPresence() {
    const flat = [];
    PRESENCE.forEach((group) => {
        group.stores.forEach((store) => {
            flat.push({ ...store, country: group.country });
        });
    });
    return flat;
}

function logoSources(store) {
    // Priority:
    //   1. Custom `store.logo` URL (editable in data.js)
    //   2. Clearbit / logo.dev / brandfetch lookup via `hotelDomain`
    // Returns an ordered list — the card tries each in sequence and
    // falls back to a serif monogram if all fail.
    const list = [];
    if (store.logo) list.push(store.logo);
    if (store.hotelDomain) {
        list.push(
            `https://img.logo.dev/${store.hotelDomain}?token=pk_X-1ZO13GSgeOoUrIuJ6GMQ&size=256&format=png`,
            `https://cdn.brandfetch.io/${store.hotelDomain}/w/512/h/512`,
            `https://logo.clearbit.com/${store.hotelDomain}?size=256`,
        );
    }
    return list;
}

function monogramOf(store) {
    // Priority:
    //   1. Custom `store.initials` (editable in data.js)
    //   2. Auto-generated from hotel / venue / name
    if (store.initials && store.initials.trim()) return store.initials.trim().toUpperCase();
    const source = store.hotel || store.venue || store.name || "";
    return source
        .replace(/[^A-Za-z\s]/g, "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
}

/**
 * A single presence card. Renders the hotel logo (tries multiple CDNs)
 * or an elegant serif monogram fallback.
 */
function PresenceCard({ store }) {
    const sources = logoSources(store);
    const [srcIdx, setSrcIdx] = useState(0);
    const [imgFailed, setImgFailed] = useState(false);
    const currentSrc = sources[srcIdx];
    const monogram = monogramOf(store);

    const onImgError = () => {
        if (srcIdx + 1 < sources.length) {
            setSrcIdx(srcIdx + 1);
        } else {
            setImgFailed(true);
        }
    };

    return (
        <div
            data-testid={`presence-card-${store.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="group relative flex-shrink-0 w-[280px] md:w-[320px] h-[360px] mx-3 md:mx-4 border border-[#1F1F1F] bg-[#0A0A0A] flex flex-col justify-between p-8 transition-all duration-500 hover:border-[#D4AF37] hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(212,175,55,0.35)] overflow-hidden"
        >
            {/* Gold shimmer sweep on hover */}
            <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1400ms] ease-out"
                style={{
                    background:
                        "linear-gradient(115deg, transparent 30%, rgba(212, 175, 55, 0.12) 50%, transparent 70%)",
                }}
            />

            {/* Top: country pill */}
            <div className="relative z-10 flex items-center justify-between">
                <span className="overline text-[10px] text-[#D4AF37] flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" strokeWidth={1.4} />
                    {store.country}
                </span>
                {store.flagship && (
                    <span className="overline text-[9px] text-[#D4AF37] flex items-center gap-1 border border-[#D4AF37]/50 px-2 py-1">
                        <Sparkles className="w-2.5 h-2.5" strokeWidth={1.6} /> Flagship
                    </span>
                )}
            </div>

            {/* Middle: logo (if available and loaded) or monogram */}
            <div className="relative z-10 flex-1 flex items-center justify-center px-4">
                {store.flagship ? (
                    <img
                        src={BRAND.logo}
                        alt="The Treasure Threads"
                        loading="lazy"
                        className="max-h-28 w-auto transition-all duration-500 opacity-90 group-hover:opacity-100"
                        style={{ filter: "brightness(0) invert(1)" }}
                    />
                ) : currentSrc && !imgFailed ? (
                    <img
                        key={currentSrc}
                        src={currentSrc}
                        alt={`${store.hotel || store.name} mark`}
                        loading="lazy"
                        onError={onImgError}
                        className="max-h-24 max-w-[80%] w-auto transition-all duration-500 opacity-90 group-hover:opacity-100"
                        style={{
                            filter: "invert(1) grayscale(100%) contrast(1.05)",
                            mixBlendMode: "screen",
                        }}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center">
                        <span className="font-serif-display text-5xl md:text-6xl text-[#F1F1EE]/85 group-hover:text-[#D4AF37] transition-colors duration-500 tracking-tight leading-none">
                            {monogram || "TCI"}
                        </span>
                        <span className="mt-3 w-8 h-px bg-[#F1F1EE]/25 group-hover:bg-[#D4AF37] transition-colors duration-500" />
                    </div>
                )}
            </div>

            {/* Bottom: store details */}
            <div className="relative z-10">
                <p className="font-serif-display text-xl md:text-2xl text-[#F1F1EE] leading-tight">
                    {store.name}
                </p>
                <div className="w-6 h-px bg-[#D4AF37] mt-3 mb-3 group-hover:w-12 transition-[width] duration-500" />
                <p className="text-[13px] text-[#B8B8B8] font-light leading-snug">
                    {store.hotel || store.venue || store.subtitle || "Boutique"}
                </p>
                <p className="mt-2 overline text-[10px] text-[#888]">{store.location}</p>
            </div>
        </div>
    );
}

/**
 * PresenceSection — auto-scrolling carousel of stores by country.
 * Pauses on hover so users can inspect a card comfortably.
 */
export default function PresenceSection() {
    const items = useMemo(flattenPresence, []);
    const countryCount = PRESENCE.length;
    const totalCount = items.length;

    return (
        <section
            data-testid="home-presence"
            className="bg-[#0A0A0A] text-[#F1F1EE] py-24 md:py-32 border-t border-b border-[#1A1A1A] overflow-hidden"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <FadeIn className="lg:col-span-7">
                        <p className="overline text-[#D4AF37]">Where You&rsquo;ll Find Us</p>
                        <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-6 leading-[1.03] text-[#F1F1EE]">
                            A quiet presence inside the world&rsquo;s most storied hotels.
                        </h2>
                    </FadeIn>
                    <FadeIn delay={150} className="lg:col-span-5 lg:pl-8">
                        <p className="text-[#B8B8B8] font-light text-base leading-relaxed max-w-md">
                            From our flagship in Chiang Mai to boutiques inside Hyatt, Ritz-Carlton, Raffles, Anantara and Marriott properties across Asia — {totalCount}&nbsp;stores across {countryCount}&nbsp;countries.
                        </p>
                        <div className="mt-8 flex items-center gap-6">
                            {PRESENCE.map((g) => (
                                <div key={g.country} className="flex items-baseline gap-2">
                                    <span className="font-serif-display text-3xl md:text-4xl text-[#F1F1EE]">
                                        {g.stores.length}
                                    </span>
                                    <span className="overline text-[10px] text-[#888]">{g.country}</span>
                                </div>
                            ))}
                        </div>
                    </FadeIn>
                </div>
            </div>

            {/* Auto-scrolling marquee. Track is duplicated for a seamless loop. */}
            <div
                className="mt-16 md:mt-20 relative"
                data-testid="presence-carousel"
                style={{
                    maskImage:
                        "linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%)",
                    WebkitMaskImage:
                        "linear-gradient(90deg, transparent 0, #000 6%, #000 94%, transparent 100%)",
                }}
            >
                <div
                    className="marquee-track flex w-max hover:[animation-play-state:paused]"
                    style={{ animationDuration: "60s" }}
                >
                    {items.map((s, i) => (
                        <PresenceCard key={`a-${i}`} store={s} />
                    ))}
                    {items.map((s, i) => (
                        <PresenceCard key={`b-${i}`} store={s} />
                    ))}
                </div>
            </div>
        </section>
    );
}
